import { skipToMessageList } from './MailList.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react';
import MailListContent from './MailListContent';
import { logMountingPerformance } from 'owa-analytics';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getNativeAdInitialPlacement, NativeAdPlacement } from 'owa-mail-ads-shared';
import { clearFilter } from 'owa-mail-filter-actions';
import { resetFocus } from 'owa-mail-focus-manager';
import * as MailModuleAutoFocusManager from 'owa-mail-focus-manager/lib/mailModuleAutoFocusManager';
import { hideMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';
import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import { MailListItemContextMenu } from 'owa-mail-list-context-menu';
import { MailListEmptyState } from 'owa-mail-list-empty-state';
import { listViewStore, MailFolderTableQuery, TableView } from 'owa-mail-list-store';
import { getFocusedFilterForTable, TableQueryType } from 'owa-mail-list-store';
import MailListItemContextMenuState from 'owa-mail-list-store/lib/store/schema/MailListItemContextMenuState';
import { messageAdListViewStatusStore } from 'owa-mail-messagead-list-store';
import { ActionSource } from 'owa-mail-store';
import { MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import store from 'owa-mail-store/lib/store/Store';
import getMailListLoadState from 'owa-mail-store/lib/utils/getMailListLoadState';
import { loadTableViewFromTableQuery } from 'owa-mail-table-loading-actions';
import { safePerformanceMeasure } from 'owa-search-diagnostics/lib/helpers/performanceTiming';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { trace } from 'owa-trace';
import * as React from 'react';
import { reactive } from 'satcheljs/lib/legacy/react';
import { addSkipLinkRegion } from 'owa-skip-link/lib/utils/addSkipLinkRegion';

const styles = require('./MailList.scss');

export interface MailListProps {
    itemContextMenuState?: MailListItemContextMenuState;
    tableViewId: string;
    styleSelectorAsPerUserSettings: string;
}

@reactive({
    itemContextMenuState: () => listViewStore.itemContextMenuState,
})
@logMountingPerformance('MailList')
@observer
export default class MailList extends React.Component<MailListProps, {}> {
    private listViewContainer: HTMLDivElement;
    private mailListContent: MailListContent;
    private oldNumRows: number = 0;

    componentDidMount() {
        MailModuleAutoFocusManager.registerComponent(
            MailModuleAutoFocusManager.FocusComponent.MailList,
            this.trySetFocus
        );
        if (!this.listViewContainer) {
            trace.error('listViewContainer should not be null');
        }

        const mailListLoadState: MailListViewState = getMailListLoadState();
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);

        this.measureInitialLoadPerformance(tableView, mailListLoadState, 'mailListMount');
        if (isFeatureEnabled('tri-scrollMoreDataPoint')) {
            this.oldNumRows = tableView.rowKeys.length;
        }
    }

    componentWillUnmount() {
        MailModuleAutoFocusManager.unregisterComponent(
            MailModuleAutoFocusManager.FocusComponent.MailList
        );
    }

    componentDidUpdate(prevProps: MailListProps) {
        const mailListLoadState: MailListViewState = getMailListLoadState();
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        const hasTableChanged = prevProps.tableViewId !== this.props.tableViewId;

        if (isFeatureEnabled('tri-scrollMoreDataPoint') && hasTableChanged) {
            this.oldNumRows = tableView.rowKeys.length;
        }

        // If the tableView has just changed, and it has been loaded, then the conversations are already in store
        // Otherwise, the state is "Loading", and we will log the datapoint once FindConversation completes.
        const conversationsOrigin = hasTableChanged
            ? 'conversationsInStore'
            : 'conversationsFetched';

        this.measureInitialLoadPerformance(tableView, mailListLoadState, conversationsOrigin);
    }

    /*
    We should show the message Ad list when all of the following conditions are true:
    1. When the view is in the other pivot, native ads will be shown either OtherInboxOnly or AllInbox flight is on
    2. When any all inbox flight is on, AllInbox flight needs to be on
    3. Search does not run
    4. listRemoved is not true in message ad list view status store. This means the message Ad list is not removed by the user manually in this session
    */
    private shouldShowMessageAdList = (tableView: TableView): boolean => {
        if (!isConsumer()) {
            return false;
        }

        if (messageAdListViewStatusStore && messageAdListViewStatusStore.listRemoved) {
            return false;
        }

        let inOtherPivot = getFocusedFilterForTable(tableView) == FocusedViewFilter.Other;
        let nativeAdInitialPlacement = getNativeAdInitialPlacement(inOtherPivot);

        return (
            (inOtherPivot &&
                (nativeAdInitialPlacement == NativeAdPlacement.OtherOnly ||
                    nativeAdInitialPlacement == NativeAdPlacement.OtherPrefer)) ||
            (tableView.tableQuery.folderId == folderNameToId('inbox') &&
                tableView.tableQuery.type != TableQueryType.Search &&
                (nativeAdInitialPlacement == NativeAdPlacement.AllInbox ||
                    (nativeAdInitialPlacement == NativeAdPlacement.OtherPrefer &&
                        !isFocusedInboxEnabled())))
        );
    };

    /**
     * Callback when the list view gains focus from the focus manager
     */
    private trySetFocus = (): boolean => {
        // This can be null when component is not fully mounted
        if (!this.listViewContainer) {
            return false;
        }

        const currentShowListPane = store.mailModule.showListPane;
        if (currentShowListPane) {
            // mailListViewContent can be null until content gets fully mounted
            // when 1) user switches folder or 2) OWA reloads
            if (this.mailListContent) {
                // Focus on content if it exists
                return this.mailListContent.trySetFocus();
            } else {
                this.listViewContainer.focus();
                return true;
            }
        }

        // Let FocusManager handle the job when listview is hidden
        return false;
    };

    /**
     * Returns flag indicating whether the document active element is contained within this MailList
     */
    private isFocusInMailList = (): boolean => {
        return this.listViewContainer && this.listViewContainer.contains(document.activeElement);
    };

    private isFocusOnMailList = (): boolean => {
        return this.listViewContainer && this.listViewContainer == document.activeElement;
    };

    /**
     * Reload the default list view for selected folder id
     */
    private reloadTable = (actionSource: ActionSource) => {
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);
        loadTableViewFromTableQuery(
            tableView.tableQuery,
            null /* loadTableViewDatapoint */,
            actionSource
        );

        // Log that retry was clicked
        logUsage('TnS_RetryLoadMailListClicked');
    };

    private setlistViewContainerRef = (ref: HTMLDivElement) => {
        this.listViewContainer = ref;
    };

    private onDragOver = (evt: React.DragEvent<EventTarget>) => {
        /* We need preventDefault so that the circle-slash ghost icon does not show and to prevent redirection in firefox */
        evt.preventDefault();
    };

    private onDrop = (evt: React.DragEvent<EventTarget>) => {
        /* We need preventDefault to prevent redirection in firefox */
        evt.preventDefault();
    };

    render() {
        const mailListViewState: MailListViewState = getMailListLoadState();
        let listViewContent: JSX.Element;
        const tableView = listViewStore.tableViews.get(this.props.tableViewId);

        // Render when table is Loaded.
        if (mailListViewState == MailListViewState.Loaded) {
            listViewContent = (
                <div onDragOver={this.onDragOver} onDrop={this.onDrop} className={styles.container}>
                    {addSkipLinkRegion(
                        loc(skipToMessageList),
                        'complementary',
                        <MailListContent
                            ref={ref => (this.mailListContent = ref)}
                            isFocusOnMailList={this.isFocusOnMailList}
                            isFocusInMailList={this.isFocusInMailList}
                            tableViewId={this.props.tableViewId}
                            onScroll={this.onMailListScroll}
                            shouldShowMessageAdList={this.shouldShowMessageAdList(tableView)}
                            styleSelectorAsPerUserSettings={
                                this.props.styleSelectorAsPerUserSettings
                            }
                        />
                    )}
                    {this.renderItemContextMenu()}
                </div>
            );
        } else {
            listViewContent = (
                <MailListEmptyState
                    tableViewId={this.props.tableViewId}
                    stateType={mailListViewState}
                    clearFilterCommand={clearFilter}
                    reloadCommand={this.reloadTable}
                    shouldShowMessageAdList={this.shouldShowMessageAdList(tableView)}
                />
            );
        }

        return (
            <div tabIndex={-1} ref={this.setlistViewContainerRef} className={styles.wrapperDiv}>
                {listViewContent}
            </div>
        );
    }

    /**
     * Renders the mail list item context menu by initiating the itemContextMenuState state in store
     */
    private renderItemContextMenu(): JSX.Element {
        const { itemContextMenuState } = this.props;

        return (
            itemContextMenuState && (
                <MailListItemContextMenu
                    anchorPoint={itemContextMenuState.anchor}
                    menuType={itemContextMenuState.menuType}
                    menuSource={itemContextMenuState.menuSource}
                    onDismiss={this.dismissContextMenu}
                    tableViewId={this.props.tableViewId}
                />
            )
        );
    }

    /**
     * Called when mail list scroll
     */
    private onMailListScroll = (ev: MouseEvent) => {
        // If context menu was open, dismiss the context menu
        if (this.props.itemContextMenuState) {
            this.dismissContextMenu(ev);
        }

        if (isFeatureEnabled('tri-scrollMoreDataPoint')) {
            const { rowKeys, totalRowsInView } = listViewStore.tableViews.get(
                this.props.tableViewId
            );
            const newNumRows = rowKeys.length;

            // Log how many total rows loaded in after user scroll and total number of rows in the view
            // This dp is TEMPORARY (as it will be very noisy)
            if (newNumRows > this.oldNumRows) {
                logUsage('TnS_ScrollLoadMore', [newNumRows, totalRowsInView]);
                this.oldNumRows = newNumRows;
            }
        }
    };

    /**
     * Dismiss the context menu and reset focus
     */
    private dismissContextMenu = (ev?: MouseEvent) => {
        hideMailItemContextMenu();
        resetFocus();
    };

    /**
     * Logs a performance datapoint with a duration indicating how much time has passed since
     * the click on the left nav that caused this MailList to render.
     *
     * DidMount is called on initial load, didUpdate when the tableViewId change.
     * The perf datapoint will be generated only when a click actually happened, and when the loading state
     * is different than "Loading"
     */
    private measureInitialLoadPerformance(
        tableView: TableView,
        loadState: MailListViewState,
        source: string
    ): void {
        if (!tableView || loadState === MailListViewState.Loading) {
            // We don't have the right info to log a render perf, so don't do anything
            return;
        }

        const measure = safePerformanceMeasure('📧 SelectFolderNodeToMailListContentRender');

        const folderTableQuery = tableView.tableQuery as MailFolderTableQuery;

        if (measure) {
            const mailListPerceivedPerformanceDataPoint = new PerformanceDatapoint(
                'PerceivedPerformance: MailListContentRender'
            );

            mailListPerceivedPerformanceDataPoint.addCustomProperty(
                'startTime',
                Math.round(measure.startTime)
            );
            mailListPerceivedPerformanceDataPoint.addCustomProperty(
                'scenarioType',
                folderTableQuery ? folderTableQuery.scenarioType : 'No Scenario'
            );
            mailListPerceivedPerformanceDataPoint.addCustomProperty('source', source);
            mailListPerceivedPerformanceDataPoint.addCustomProperty('loadingState', loadState);
            mailListPerceivedPerformanceDataPoint.addCustomProperty(
                'tableQueryType',
                folderTableQuery ? folderTableQuery.type : 'No Type'
            );
            mailListPerceivedPerformanceDataPoint.end(Math.round(measure.duration));
        }
    }
}
